import { AuthContext, trelloStorageService } from "@harvestr-api/common";
import { gqlTrelloCardCreateWithoutDiscoveryInput } from "@harvestr-api/models";
import { logger, parallelPromiseHelper } from "@harvestr-api/utils";
import crypto from "crypto";
import request from "request-promise";
import {
    getTrelloClient,
    getTrelloToken
} from "../helpers/trello-client.helper";

export const trelloWebhookService = {
    async registerWebhookOnCard(
        trelloCard: gqlTrelloCardCreateWithoutDiscoveryInput,
        ctx: AuthContext
    ) {
        const trello = await getTrelloClient(ctx);
        return new Promise(resolve => {
            trello.addWebhook(
                `webhook watching card '${trelloCard.trelloName}' (#${trelloCard.trelloId})`,
                `${process.env.INTEGRATIONS_API_URL}/devtools/trello/sync/card?project_id=${ctx.project_id}`,
                trelloCard?.trelloId,
                async (error, webhookCreated) => {
                    if (error) {
                        resolve(error);
                    } else if (webhookCreated?.id) {
                        resolve(webhookCreated.id);
                    } else {
                        if (
                            webhookCreated === null ||
                            (typeof webhookCreated === "string" &&
                                webhookCreated
                                    .toLowerCase()
                                    .includes("already exists"))
                        ) {
                            console.log("already exists");
                            const webhookId = await trelloWebhookService.getRegisteredWebhookIdOfCard(
                                trelloCard,
                                ctx
                            );
                            resolve(webhookId);
                        } else if (
                            typeof webhookCreated === "object" &&
                            webhookCreated.message &&
                            webhookCreated.message.includes("ESOCKETTIMEDOUT")
                        ) {
                            console.log("trello.addWebhook() : TIMEOUT");
                            resolve(null);
                        }
                    }
                }
            );
        });
    },

    async registerAllWebhooks(where: { project_id: string }) {
        const cardsWebhookLess = await trelloStorageService.getCardsOfProjectWithoutWebhook(
            where
        );
        await parallelPromiseHelper.uploadData(
            cardsWebhookLess || [],
            async cardsChunk => {
                return Promise.all(
                    cardsChunk.reduce((promises: any[], card) => {
                        promises.push(
                            trelloWebhookService
                                .registerWebhookOnCard(card, {
                                    type: "INTEGRATION_TRELLO",
                                    project_id: where.project_id
                                })
                                .catch(e => {
                                    return null;
                                })
                        );
                        return promises;
                    }, [])
                );
            },
            10
        );
    },

    async unregisterAllWebhooks(ctx: AuthContext, gotTokenAlready?: any) {
        // Trello exposes a API endpoint that provides all currently registered webhooks.
        // This helps us to not bypass any webhook due to a issue with webhookID reference broken.

        const wholeSetWebhooksRegistered = await trelloWebhookService.getAllWebhooks(
            ctx,
            gotTokenAlready
        );

        const HARVESTR_ID_MEMBER = "Harvestr Trello Integration";
        const allWebhooksRegistered =
            wholeSetWebhooksRegistered.find(
                item => item.identifier === HARVESTR_ID_MEMBER
            )?.webhooks || [];

        allWebhooksRegistered.length > 0 &&
            (await parallelPromiseHelper.uploadData(
                allWebhooksRegistered,
                async webhooksChunk => {
                    return Promise.all(
                        webhooksChunk.map((webhook: any) =>
                            trelloWebhookService.unregisterWebhookOnCard(
                                webhook.id as string,
                                webhook.idModel,
                                ctx,
                                gotTokenAlready
                            )
                        )
                    );
                },
                10
            ));

        return Promise.resolve();
    },

    isWebhookSignatureValid(
        event: { body: any; headers: any },
        project_id: string
    ): boolean {
        const base64Digest = data =>
            crypto
                .createHmac("sha1", <string>process.env.TRELLO_OAUTH_SECRET)
                .update(data)
                .digest("base64");
        const payload = event.body;
        const callbackUrl = `${process.env.INTEGRATIONS_API_URL}/devtools/trello/sync/card?project_id=${project_id}`;
        return crypto.timingSafeEqual(
            Buffer.from(base64Digest(base64Digest(`${payload}${callbackUrl}`))),
            Buffer.from(base64Digest(event.headers["x-trello-webhook"]))
        );
    },

    async getRegisteredWebhookIdOfCard(
        trelloCard: gqlTrelloCardCreateWithoutDiscoveryInput,
        ctx: AuthContext
    ) {
        logger.info("trelloWebhookService.attachRegisteredWebhookToCard()");
        const listWebhooks = (
            (await trelloWebhookService.getAllWebhooks(ctx)) || "[]"
        ).reduce((acc, node) => (acc = [...acc, ...(node.webhooks || [])]), []);
        const foundWebhook = (listWebhooks || []).find(
            wh => wh.idModel === trelloCard.trelloId
        );

        if (foundWebhook) {
            return foundWebhook.id;
        }
        return null;
    },

    async unregisterWebhookOnCard(
        webhookId: string,
        trelloId: string,
        ctx: AuthContext,
        gotTokenAlready?: any
    ) {
        console.log("trello.unregisterWebhookOnCard()");
        if (webhookId === undefined) {
            console.log(
                "trello.unregisterWebhookOnCard() :  webhookid undefined. stop"
            );
            return;
        }
        const trello = await getTrelloClient(ctx, gotTokenAlready);

        trello
            .deleteWebhook(webhookId)
            .catch(async err => {
                if (err.statusCode == 404) {
                    await trelloStorageService.updateCard(trelloId, {
                        trelloWebhookId: null
                    });
                }
                return true;
            })
            .then(async () => {
                await trelloStorageService.updateCard(trelloId, {
                    trelloWebhookId: null
                });
            });
    },

    async getAllWebhooks(ctx: AuthContext, gotTokenAlready?: any) {
        let token;
        if (gotTokenAlready === undefined) {
            token = await getTrelloToken(ctx);
        } else {
            token = gotTokenAlready;
        }
        const options = {
            method: "GET",
            url: `https://api.trello.com/1/members/me/tokens?webhooks=true&key=${process.env.TRELLO_KEY}&token=${token}`,
            headers: {
                "Content-Type": "application/json"
            }
        };

        return request(options).then(data => JSON.parse(data || "[]"));
    }
};
